import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Tabs,
    Tab,
    Grid,
    Chip,
    Pagination,
} from '@mui/material';
import { FiArrowLeft } from 'react-icons/fi';
import { IoMdAddCircleOutline } from 'react-icons/io';
import BannerModal from '../../components/MobileContent/BannerModal';
import LayoutCard from '../../components/MobileContent/LayoutCard';
import LayoutForm from '../../components/MobileContent/LayoutForm';
import BannerCard from '../../components/MobileContent/BannerCard';
import TabsCard from '../../components/MobileContent/TabCard';
import TabForm from '../../components/MobileContent/TabForm';
import SubcategoryCard from '../../components/MobileContent/SubcategoryCard';
import SubcategoryForm from '../../components/MobileContent/SubcategoryForm';
import { postData, postFileUpload, putData, getData, deleteData } from '../../Webservice/Webservice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const contentTabs = [
    { value: 'banner', label: 'Banners' },
    { value: 'layout', label: 'Layout Sections' },
    { value: 'tab', label: 'Tab Section' },
    { value: 'subcategory', label: 'Subcategory Section' },
];

const MobileContent = () => {
    const [activeTab, setActiveTab] = useState('banner');
    const navigate = useNavigate();
    const [bannerModalOpen, setBannerModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [layoutGalleryOpen, setLayoutGalleryOpen] = useState(false);
    const [layoutGalleryData, setLayoutGalleryData] = useState(null);
    const [layoutFormOpen, setLayoutFormOpen] = useState(false);
    const [editingLayout, setEditingLayout] = useState(null);
    const [layouts, setLayouts] = useState([]);
    const [tabsCardDetailsOpen, setTabsCardDetailsOpen] = useState(false);
    const [tabsCardDetailsData, setTabsCardDetailsData] = useState(null);
    const [tabFormOpen, setTabFormOpen] = useState(false);
    const [editingTab, setEditingTab] = useState(null);
    const [tabs, setTabs] = useState([]);
    const [subcategoryFormOpen, setSubcategoryFormOpen] = useState(false);
    const [editingSubcategory, setEditingSubcategory] = useState(null);
    const [subcategories, setSubcategories] = useState([]);
    const [bannerMeta, setBannerMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
    const [layoutMeta, setLayoutMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
    const [tabMeta, setTabMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });
    const [subcategoryMeta, setSubcategoryMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 10 });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        if (newValue === 'banner') {
            setBannerMeta(prev => ({ ...prev, page: 1 }));
        } else if (newValue === 'layout') {
            setLayoutMeta(prev => ({ ...prev, page: 1 }));
        } else if (newValue === 'tab') {
            setTabMeta(prev => ({ ...prev, page: 1 }));
        } else if (newValue === 'subcategory') {
            setSubcategoryMeta(prev => ({ ...prev, page: 1 }));
        }
    };

    // Fetch banners with pagination
    const fetchBanners = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/banner/list', { page, limit }, token, false);
            
            if (response.banner && Array.isArray(response.banner)) {
                setBanners(response.banner);
                setBannerMeta(response.meta || { page: 1, totalPages: 1, total: 0, limit });
            } else if (response.banners && Array.isArray(response.banners)) {
                setBanners(response.banners);
                setBannerMeta(response.meta || { page: 1, totalPages: 1, total: 0, limit });
            } else {
                setBanners([]);
                setBannerMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
            setBanners([]);
            setBannerMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch layouts with pagination
    const fetchLayouts = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/layout/list', { page, limit, productLimit: 100 }, token);
            
            if (response && Array.isArray(response.layout)) {
                const transformedLayouts = response.layout.map(layout => ({
                    id: layout._id || layout.id,
                    _id: layout._id,
                    title: layout.title,
                    type: layout.title,
                    layout: layout.layoutId?.name || 'Unknown Layout',
                    layoutId: layout.layoutId?._id || '',
                    position: layout.position,
                    status: (layout.status || '').toLowerCase(),
                    products: Array.isArray(layout.product_id) ? layout.product_id.map(product => ({
                        _id: product._id,
                        name: product.title,
                        title: product.title,
                        image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                    })) : [],
                }));
                setLayouts(transformedLayouts);
                setLayoutMeta(response.meta || layoutMeta);
            } else {
                setLayouts([]);
                setLayoutMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching layouts:', error);
            setLayouts([]);
            setLayoutMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch tabs with pagination
    const fetchTabs = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/tabs/list', { page, limit, productLimit: 100 }, token);
            
            if (response && Array.isArray(response.tabs)) {
                const transformedTabs = response.tabs.map(tab => ({
                    id: tab._id,
                    _id: tab._id,
                    name: tab.name,
                    status: tab.status,
                    position: tab.position,
                    showTo: tab.showTo || 'all',
                    product_ids: Array.isArray(tab.product_id) ? tab.product_id.map(p => p._id || p.id || p) : [],
                    products: Array.isArray(tab.product_id) ? tab.product_id.map(product => ({
                        _id: product._id || product.id || product,
                        id: product._id || product.id || product,
                        name: product.title || product.name,
                        title: product.title || product.name,
                        image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                    })) : [],
                }));
                setTabs(transformedTabs);
                setTabMeta(response.meta || tabMeta);
            } else {
                setTabs([]);
                setTabMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching tabs:', error);
            setTabs([]);
            setTabMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch subcategories with pagination
    const fetchSubcategories = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('homepage/homecategory/list', { page, limit }, token);
            
            if (response && Array.isArray(response.homecategory)) {
                setSubcategories(response.homecategory);
                setSubcategoryMeta(response.meta || { page: 1, totalPages: 1, total: 0, limit });
            } else {
                console.error('Incomplete homecategory response:', response);
                setSubcategories([]);
                setSubcategoryMeta({ page: 1, totalPages: 1, total: 0, limit });
            }
        } catch (error) {
            console.error('Error fetching subcategories:', error);
            setSubcategories([]);
            setSubcategoryMeta({ page: 1, totalPages: 1, total: 0, limit });
        } finally {
            setLoading(false);
        }
    };

    // Fetch data based on active tab
    useEffect(() => {
        if (activeTab === 'banner') {
            fetchBanners(bannerMeta.page, bannerMeta.limit);
        } else if (activeTab === 'layout') {
            fetchLayouts(layoutMeta.page, layoutMeta.limit);
        } else if (activeTab === 'tab') {
            fetchTabs(tabMeta.page, tabMeta.limit);
        } else if (activeTab === 'subcategory') {
            fetchSubcategories(subcategoryMeta.page, subcategoryMeta.limit);
        }
    }, [activeTab, bannerMeta.page, layoutMeta.page, tabMeta.page, subcategoryMeta.page]);

    // Handle page change for banners
    const handleBannerPageChange = (event, newPage) => {
        setBannerMeta(prev => ({ ...prev, page: newPage }));
    };

    // Handle page change for layouts
    const handleLayoutPageChange = (event, newPage) => {
        setLayoutMeta(prev => ({ ...prev, page: newPage }));
    };

    // Handle page change for tabs
    const handleTabPageChange = (event, newPage) => {
        setTabMeta(prev => ({ ...prev, page: newPage }));
    };

    // Handle page change for subcategories
    const handleSubcategoryPageChange = (event, newPage) => {
        setSubcategoryMeta(prev => ({ ...prev, page: newPage }));
    };

    // Banner handlers
    const handleBannerOpen = (banner = null) => {
        setEditingBanner(banner);
        setBannerModalOpen(true);
    };

    const handleBannerClose = () => {
        setBannerModalOpen(false);
        setEditingBanner(null);
    };

    const handleBannerDelete = async (banner) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/banner/delete/${banner._id || banner.id}`, token);
            
            if (response) {
                fetchBanners(bannerMeta.page, bannerMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting banner:', error);
        }
    };

    const handleBannerSubmit = async (bannerData) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            const formData = new FormData();
            if (bannerData.image) {
                if (bannerData.image instanceof File) {
                    formData.append('image', bannerData.image);
                } else {
                    // It's already a URL, send it as a string for persistence
                    formData.append('image', bannerData.image);
                }
            }
            formData.append('type', bannerData.type);
            formData.append('link', bannerData.link);
            formData.append('status', bannerData.status);
            formData.append('position', bannerData.position);

            let response;
            if (editingBanner) {
                formData.append('id', editingBanner._id);
                response = await putData(`homepage/banner/update/${editingBanner._id}`, formData, token);
            } else {
                response = await postFileUpload('homepage/banner/add', formData, token);
            }
            
            if (response) {
                fetchBanners(bannerMeta.page, bannerMeta.limit);
            }
            handleBannerClose();
        } catch (error) {
            console.error('Error handling banner:', error);
        }
    };

    // Layout handlers
    const handleLayoutEdit = (layout) => {
        setEditingLayout(layout);
        setLayoutFormOpen(true);
    };

    const handleLayoutDelete = async (layout) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/layout/delete/${layout._id || layout.id}`, token);
            
            if (response) {
                fetchLayouts(layoutMeta.page, layoutMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting layout:', error);
        }
    };

    const handleLayoutOpenGallery = async (layout) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`homepage/layout/${layout._id || layout.id}`, {}, token, false);
            
            if (response) {
                const rawData = response.layout || response;
                const transformedLayout = {
                    _id: rawData._id,
                    type: rawData.title,
                    status: rawData.status,
                    position: rawData.position,
                    layout: rawData.layoutId?.name || 'Unknown Layout',
                    layoutId: rawData.layoutId?._id || '',
                    products: Array.isArray(rawData.product_id) ? rawData.product_id.map(product => ({
                        _id: product._id,
                        name: product.title,
                        image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                        price: product.price
                    })) : [],
                };
                setLayoutGalleryData(transformedLayout);
            } else {
                 setLayoutGalleryData(layout);
            }
            setLayoutGalleryOpen(true);
        } catch (error) {
            console.error('Error fetching layout details:', error);
            setLayoutGalleryData(layout);
            setLayoutGalleryOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleLayoutCloseGallery = () => {
        setLayoutGalleryOpen(false);
        setLayoutGalleryData(null);
    };

    const handleLayoutOpenForm = () => {
        setEditingLayout(null);
        setLayoutFormOpen(true);
    };

    const handleLayoutCloseForm = () => {
        setLayoutFormOpen(false);
        setEditingLayout(null);
    };

    const handleLayoutSubmit = async (formValues) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            if (!formValues.title || !formValues.layoutId) {
                toast.error("Title and Layout ID are required");
                return;
            }

            const layoutData = {
                title: formValues.title,
                layoutId: formValues.layoutId,
                position: formValues.position,
                product_id: formValues.product_ids,
                status: (formValues.status || 'active').toLowerCase(),
            };

            let response;
            if (editingLayout) {
                response = await putData(`homepage/layout/update/${editingLayout._id || editingLayout.id}`, layoutData, token);
            } else {
                response = await postData('homepage/layout/add', layoutData, token);
            }
            
            if (response) {
                toast.success(editingLayout ? "Layout updated successfully" : "Layout added successfully");
                fetchLayouts(layoutMeta.page, layoutMeta.limit);
                handleLayoutCloseForm();
            }
        } catch (error) {
            console.error('Error handling layout:', error);
        }
    };

    // Tab handlers
    const handleTabOpen = (tab = null) => {
        setEditingTab(tab);
        setTabFormOpen(true);
    };

    const handleTabClose = () => {
        setTabFormOpen(false);
        setEditingTab(null);
    };

    const handleTabSubmit = async (tabData) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            if (!tabData.name) {
                toast.error("Tab name is required");
                return;
            }

            const tabPayload = {
                name: tabData.name,
                status: (tabData.status || 'active').toLowerCase(),
                position: tabData.position,
                showTo: (tabData.showTo || 'all').toLowerCase(),
                product_id: tabData.product_id,
            };

            let response;
            if (editingTab) {
                response = await putData(`homepage/tabs/update/${editingTab._id || editingTab.id}`, tabPayload, token);
            } else {
                response = await postData('homepage/tabs/add', tabPayload, token);
            }
            
            if (response) {
                toast.success(editingTab ? "Tab updated successfully" : "Tab added successfully");
                fetchTabs(tabMeta.page, tabMeta.limit);
                handleTabClose();
            }
        } catch (error) {
            console.error('Error handling tab:', error);
        }
    };

    // Product handlers for Tabs
    const handleProductEdit = (product) => {
        setEditingTab(product);
        setTabFormOpen(true);
    };

    const handleProductDelete = async (product) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/tabs/delete/${product._id || product.id}`, token);
            
            if (response) {
                fetchTabs(tabMeta.page, tabMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting tab:', error);
        }
    };

    const handleTabsCardOpenDetails = async (product) => {
        try {
            setLoading(true);
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`homepage/tabs/${product._id || product.id}`, {}, token, false);
            
            if (response) {
                setTabsCardDetailsData(response.tab || response);
            } else {
                 setTabsCardDetailsData(product);
            }
             setTabsCardDetailsOpen(true);
        } catch (error) {
            console.error('Error fetching tab details:', error);
            setTabsCardDetailsData(product);
            setTabsCardDetailsOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleTabsCardCloseDetails = () => {
        setTabsCardDetailsOpen(false);
        setTabsCardDetailsData(null);
    };

    // Subcategory handlers
    const handleSubcategoryOpen = (subcategory = null) => {
        setEditingSubcategory(subcategory);
        setSubcategoryFormOpen(true);
    };

    const handleSubcategoryClose = () => {
        setSubcategoryFormOpen(false);
        setEditingSubcategory(null);
    };

    const handleSubcategorySubmit = async (subcategoryData) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            
            if (!subcategoryData.linkId) {
                toast.error("Please select a category/subcategory");
                return;
            }

            if (!editingSubcategory && !subcategoryData.image) {
                toast.error("Image is required for new items");
                return;
            }

            const formData = new FormData();
            if (subcategoryData.image) {
                if (subcategoryData.image instanceof File) {
                    formData.append('image', subcategoryData.image);
                } else {
                    // It's a URL string, send it back for persistence
                    formData.append('image', subcategoryData.image);
                }
            }
            formData.append('type', subcategoryData.type);
            formData.append('linkId', subcategoryData.linkId);
            formData.append('status', (subcategoryData.status || 'active').toLowerCase());
            formData.append('showTo', (subcategoryData.showTo || 'all').toLowerCase());
            formData.append('position', subcategoryData.position);

            let response;
            if (editingSubcategory) {
                formData.append('id', editingSubcategory._id);
                response = await putData(`homepage/homecategory/update/${editingSubcategory._id}`, formData, token);
            } else {
                response = await postFileUpload('homepage/homecategory/add', formData, token);
            }
            
            if (response) {
                toast.success(editingSubcategory ? "Home Category updated successfully" : "Home Category added successfully");
                fetchSubcategories(subcategoryMeta.page, subcategoryMeta.limit);
                handleSubcategoryClose();
            }
        } catch (error) {
            console.error('Error handling subcategory:', error);
        }
    };

    const handleSubcategoryDelete = async (subcategory) => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await deleteData(`homepage/homecategory/delete/${subcategory._id || subcategory.id}`, token);
            
            if (response) {
                fetchSubcategories(subcategoryMeta.page, subcategoryMeta.limit);
            }
        } catch (error) {
            console.error('Error deleting subcategory:', error);
        }
    };

    // Get active datafhandleLayoutOpenGallery
    const getActiveData = () => {
        switch (activeTab) {
            case 'banner':
                return banners;
            case 'layout':
                return layouts;
            case 'tab':
                return tabs;
            case 'subcategory':
                return subcategories;
            default:
                return banners;
        }
    };

    // Get add button text
    const getAddButtonText = () => {
        switch (activeTab) {
            case 'banner':
                return 'Add Banner';
            case 'layout':
                return 'Add Layout';
            case 'tab':
                return 'Add Tabs';
            case 'subcategory':
                return 'Add Home Category';
            default:
                return 'Add Content';
        }
    };

    // Render card component
    const renderCard = (item) => {
        switch (activeTab) {
            case 'banner':
                return (
                    <BannerCard 
                        banner={item} 
                        onEdit={handleBannerOpen}
                        onDelete={handleBannerDelete}
                    />
                );
            case 'layout':
                return (
                    <LayoutCard 
                        layout={item} 
                        onEdit={handleLayoutEdit}
                        onDelete={handleLayoutDelete}
                        onOpenGallery={handleLayoutOpenGallery}
                    />
                );
            case 'tab':
                return (
                    <TabsCard 
                        product={item} 
                        onEdit={handleProductEdit}
                        onDelete={handleProductDelete}
                        onOpenDetails={handleTabsCardOpenDetails}
                    />
                );
            case 'subcategory':
                return (
                    <SubcategoryCard 
                        subcategory={item} 
                        onEdit={handleSubcategoryOpen}
                        onDelete={handleSubcategoryDelete}
                    />
                );
            default:
                return null;
        }
    };

    const activeData = getActiveData();

    // Layout Gallery View
    if (layoutGalleryOpen && layoutGalleryData) {
        const galleryItems = (Array.isArray(layoutGalleryData?.products) && layoutGalleryData.products.length > 0
            ? layoutGalleryData.products.map((product, index) => ({ 
                key: index, 
                image: product.image, 
                name: product.name, 
                // price: product.price || 'N/A' 
            }))
            : []);

        return (
            <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                <Button 
                    startIcon={<FiArrowLeft />} 
                    onClick={handleLayoutCloseGallery}
                    sx={{ 
                        mb: 3,
                        marginLeft: "15px",
                        color: '#64748b',
                        '&:hover': {
                            backgroundColor: '#f1f5f9',
                            color: '#334155'
                        }
                    }}
                >
                    Back to Layout
                </Button>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={3} sx={{ marginLeft: "15px", mb: 2 }}>
                    <Box>
                        <Typography variant="h3" fontWeight={700} color="#0f172a" mb={1} sx={{ fontFamily: "'mulish', sans-serif" }}>
                            {layoutGalleryData?.type}
                        </Typography>
                        <Typography variant="body1" color="#64748b">
                            {layoutGalleryData?.layout}
                        </Typography>
                    </Box>
                    <Chip
                        label={layoutGalleryData?.status}
                        size="large"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            height: '40px',
                            px: 2,
                            backgroundColor: layoutGalleryData.status === 'active' ? '#1f6322ff' : '#f18d2eff',
                            color: 'white',
                        }}
                    />
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px' }}>
                    {galleryItems.length > 0 ? galleryItems.map((item) => (
                        <Box key={item.key} 
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'column', 
                                alignItems: 'stretch',
                                borderRadius: '8px', 
                                overflow: 'hidden',
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%' }}>
                                <img src={item.image} alt={item.name ? `${item.name}` : `Thumbnail ${item.key + 1}`} style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                            {(item.name || item.price) && (
                                <Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    {item.name && (<Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2, color: '#0f172a' }}>{item.name}</Typography>)}
                                    {item.price && (<Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#111827' }}>{item.price}</Typography>)}
                                </Box>
                            )}
                        </Box>
                    )) : (
                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', color: '#64748b' }}>
                            No products available for this layout.
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    // TabsCard Details View
    if (tabsCardDetailsOpen && tabsCardDetailsData) {
        const productItems = (Array.isArray(tabsCardDetailsData?.product_id) && tabsCardDetailsData.product_id.length > 0
            ? tabsCardDetailsData.product_id.map((product, index) => ({ 
                key: index, 
                image: product.product_images && product.product_images.length > 0 ? product.product_images[0] : null,
                name: product.title,
            }))
            : []);

        return (
            <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                <Button 
                    startIcon={<FiArrowLeft />} 
                    onClick={handleTabsCardCloseDetails}
                    sx={{ 
                        mb: 3,
                        marginLeft: "15px",
                        color: '#64748b',
                        '&:hover': {
                            backgroundColor: '#f1f5f9',
                            color: '#334155'
                        }
                    }}
                >
                    Back to Tab Section
                </Button>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={3} sx={{ marginLeft: "15px", mb: 2 }}>
                    <Box>
                        <Typography variant="h3" fontWeight={700} color="#0f172a" mb={1} sx={{ fontFamily: "'mulish', sans-serif" }}>
                            {tabsCardDetailsData?.name}
                        </Typography>
                    </Box>
                    <Chip
                        label={tabsCardDetailsData?.status}
                        size="large"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            height: '40px',
                            px: 2,
                            backgroundColor: tabsCardDetailsData.status === 'active' ? '#1f6322ff' : '#f18d2eff',
                            color: 'white',
                        }}
                    />
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px' }}>
                    {productItems.length > 0 ? productItems.map((item) => (
                        <Box key={item.key} 
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'column', 
                                alignItems: 'stretch',
                                borderRadius: '8px', 
                                overflow: 'hidden',
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            <Box sx={{ position: 'relative', width: '100%', paddingBottom: '100%' }}>
                                <img 
                                    src={item.image || '/img/placeholder.png'} 
                                    alt={item.name ? `${item.name}` : `Product ${item.key + 1}`} 
                                    style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }} 
                                />
                            </Box>
                            {item.name && (
                                <Box sx={{ p: 1.2, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2, color: '#0f172a' }}>
                                        {item.name}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )) : (
                        <Typography sx={{ fontFamily: 'Mulish, sans-serif', color: '#64748b' }}>
                            No products available for this tab.
                        </Typography>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box p={2} style={{ background: '#f8fafc', borderRadius: '10px', position: 'relative' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                        color: '#1a202c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontFamily: 'Mulish, sans-serif',
                    }}
                >
                    Mobile Content Management
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<IoMdAddCircleOutline />}
                    onClick={() => {
                        if (activeTab === 'banner') {
                            handleBannerOpen();
                        } else if (activeTab === 'layout') {
                            handleLayoutOpenForm();
                        } else if (activeTab === 'tab') {
                            handleTabOpen();
                        } else if (activeTab === 'subcategory') {
                            handleSubcategoryOpen();
                        }
                    }}
                    sx={{
                        color: 'black',
                        fontFamily: 'Mulish, sans-serif',
                        border: '1px solid black',
                        background: 'rgb(255 209 2)',
                        fontWeight: 600,
                        '&:hover': {
                            background: 'rgb(255 209 2)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        },
                    }}
                >
                    {getAddButtonText()}
                </Button>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{
                        '& .MuiTabs-indicator': {
                            display: 'none',
                        },
                        '& .MuiTabs-scroller': {
                            padding: '8px 0',
                        },
                    }}
                >
                    {contentTabs.map((tab) => (
                        <Tab
                            key={tab.value}
                            label={tab.label}
                            value={tab.value}
                            sx={{
                                fontFamily: 'Mulish, sans-serif',
                                fontWeight: 800,
                                textTransform: 'capitalize',
                                borderRadius: '8px',
                                mx: 0.5,
                                py: 1,
                                px: 2,
                                color: activeTab === tab.value ? 'black' : 'text.secondary',
                                backgroundColor: activeTab === tab.value ? 'rgb(255 209 2 / 20%)' : 'transparent',
                                border: '1px solid',
                                borderColor: activeTab === tab.value ? 'rgb(255 209 2)' : 'divider',
                                '&.Mui-selected': {
                                    color: 'black',
                                    backgroundColor: 'rgb(255 209 2)',
                                    borderColor: 'black',
                                },
                                '&:hover': {
                                    backgroundColor: 'rgb(255 209 2 / 50%)',
                                },
                            }}
                        />
                    ))}
                </Tabs>
            </Box>

            {!(activeTab === 'layout' && layoutFormOpen) && !(activeTab === 'tab' && tabFormOpen) && (
                <Grid container spacing={2}>
                    {loading && (activeTab === 'banner' || activeTab === 'layout' || activeTab === 'tab' || activeTab === 'subcategory') ? (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 8,
                                }}
                            >
                                <Typography variant="h6" sx={{ fontFamily: 'Mulish, sans-serif' }}>
                                    Loading {activeTab === 'banner' ? 'banners' : activeTab === 'layout' ? 'layouts' : activeTab === 'tab' ? 'tabs' : 'home categories'}...
                                </Typography>
                            </Box>
                        </Grid>
                    ) : activeData.length > 0 ? (
                        <>
                            {activeData.map((item) => (
                                <Grid 
                                    item 
                                    xs={12} 
                                    sm={activeTab === 'banner' ? 4 : activeTab === 'tab' ? 4 : activeTab === 'subcategory' ? 3 : 6} 
                                    key={item._id || item.id}
                                >
                                    {renderCard(item)}
                                </Grid>
                            ))}
                            {activeTab === 'banner' && bannerMeta.total > 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={bannerMeta.totalPages}
                                            page={bannerMeta.page}
                                            onChange={handleBannerPageChange}
                                            color="primary"
                                            shape="rounded"
                                            disabled={loading}
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                    <Typography 
                                        variant="caption" 
                                        color="textSecondary" 
                                        sx={{ mt: 1, display: 'block', textAlign: 'center', fontFamily: 'Mulish, sans-serif' }}
                                    >
                                        Showing {(bannerMeta.page - 1) * bannerMeta.limit + 1} to {Math.min(bannerMeta.page * bannerMeta.limit, bannerMeta.total)} of {bannerMeta.total} banners
                                    </Typography>
                                </Grid>
                            )}
                            {activeTab === 'layout' && layoutMeta.total > 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={layoutMeta.totalPages}
                                            page={layoutMeta.page}
                                            onChange={handleLayoutPageChange}
                                            color="primary"
                                            shape="rounded"
                                            disabled={loading}
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                    <Typography 
                                        variant="caption" 
                                        color="textSecondary" 
                                        sx={{ mt: 1, display: 'block', textAlign: 'center', fontFamily: 'Mulish, sans-serif' }}
                                    >
                                        Showing {(layoutMeta.page - 1) * layoutMeta.limit + 1} to {Math.min(layoutMeta.page * layoutMeta.limit, layoutMeta.total)} of {layoutMeta.total} layouts
                                    </Typography>
                                </Grid>
                            )}
                            {activeTab === 'tab' && tabMeta.total > 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={tabMeta.totalPages}
                                            page={tabMeta.page}
                                            onChange={handleTabPageChange}
                                            color="primary"
                                            shape="rounded"
                                            disabled={loading}
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                    <Typography 
                                        variant="caption" 
                                        color="textSecondary" 
                                        sx={{ mt: 1, display: 'block', textAlign: 'center', fontFamily: 'Mulish, sans-serif' }}
                                    >
                                        Showing {(tabMeta.page - 1) * tabMeta.limit + 1} to {Math.min(tabMeta.page * tabMeta.limit, tabMeta.total)} of {tabMeta.total} tabs
                                    </Typography>
                                </Grid>
                            )}
                            {activeTab === 'subcategory' && subcategoryMeta.total > 0 && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                        <Pagination
                                            count={subcategoryMeta.totalPages}
                                            page={subcategoryMeta.page}
                                            onChange={handleSubcategoryPageChange}
                                            color="primary"
                                            shape="rounded"
                                            disabled={loading}
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                    <Typography 
                                        variant="caption" 
                                        color="textSecondary" 
                                        sx={{ mt: 1, display: 'block', textAlign: 'center', fontFamily: 'Mulish, sans-serif' }}
                                    >
                                        Showing {(subcategoryMeta.page - 1) * subcategoryMeta.limit + 1} to {Math.min(subcategoryMeta.page * subcategoryMeta.limit, subcategoryMeta.total)} of {subcategoryMeta.total} subcategories
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    ) : (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 8,
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                }}
                            >
                                <img
                                    src="/img/nodata.png"
                                    alt="No content"
                                    style={{ width: '200px', marginBottom: '16px' }}
                                />
                                <Typography
                                    variant="h6"
                                    color="textSecondary"
                                    sx={{ fontFamily: 'Mulish, sans-serif' }}
                                >
                                    No {contentTabs.find((tab) => tab.value === activeTab)?.label} Found
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{
                                        mt: 1,
                                        fontFamily: 'Mulish, sans-serif',
                                        textAlign: 'center',
                                        maxWidth: '400px',
                                    }}
                                >
                                    There are no{' '}
                                    {contentTabs.find((tab) => tab.value === activeTab)?.label.toLowerCase()} in the
                                    system.
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            <BannerModal
                open={bannerModalOpen}
                handleClose={handleBannerClose}
                handleSubmit={handleBannerSubmit}
                isEdit={!!editingBanner}
                editingBanner={editingBanner}
            />

            {activeTab === 'layout' && layoutFormOpen && (
                <Box sx={{ mb: 2 }}>
                    <LayoutForm
                        open={true}
                        inline={true}
                        handleClose={handleLayoutCloseForm}
                        handleSubmit={handleLayoutSubmit}
                        isEdit={!!editingLayout}
                        editingLayout={editingLayout}
                    />
                </Box>
            )}

            {activeTab === 'tab' && tabFormOpen && (
                <Box sx={{ mb: 2 }}>
                    <TabForm
                        open={true}
                        inline={true}
                        handleClose={handleTabClose}
                        handleSubmit={handleTabSubmit}
                        isEdit={!!editingTab}
                        editingTab={editingTab}
                    />
                </Box>
            )}

            <SubcategoryForm
                open={subcategoryFormOpen}
                handleClose={handleSubcategoryClose}
                handleSubmit={handleSubcategorySubmit}
                isEdit={!!editingSubcategory}
                editingSubcategory={editingSubcategory}
            />
        </Box>
    );
};

export default MobileContent;